import React, { createContext, useContext, useCallback, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginScopes, isAuthConfigured } from './msalConfig';
import { setAuthTokenGetter } from '../services/api';

interface AuthContextValue {
  isAuthenticated: boolean;
  isAuthEnabled: boolean;
  userName: string | null;
  login: () => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
}

// Default context for when auth is not configured
const defaultAuthContext: AuthContextValue = {
  isAuthenticated: false,
  isAuthEnabled: false,
  userName: null,
  login: async () => {},
  logout: () => {},
  getAccessToken: async () => null,
};

const AuthContext = createContext<AuthContextValue>(defaultAuthContext);

export const useAuth = () => useContext(AuthContext);

/**
 * Inner provider that uses MSAL hooks (must be inside MsalProvider).
 */
const MsalAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const userName = accounts[0]?.name || accounts[0]?.username || null;

  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginScopes);
    } catch (error) {
      console.error('Login failed:', error);
    }
  }, [instance]);

  const logout = useCallback(() => {
    // Clear MSAL session storage and reload for a clean local logout.
    sessionStorage.clear();
    window.location.replace(window.location.origin);
  }, []);

  // Return the ID token (not access token) — it contains group claims for backend authorization.
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!accounts[0]) return null;
    try {
      const response = await instance.acquireTokenSilent({
        ...loginScopes,
        account: accounts[0],
      });
      return response.idToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          await instance.acquireTokenRedirect(loginScopes);
          return null;
        } catch (redirectError) {
          console.error('Token acquisition failed:', redirectError);
          return null;
        }
      }
      console.error('Silent token acquisition failed:', error);
      return null;
    }
  }, [instance, accounts]);

  useEffect(() => {
    setAuthTokenGetter(getAccessToken);
  }, [getAccessToken]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAuthEnabled: true,
        userName,
        login,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Auth provider that wraps MsalAuthProvider when configured,
 * or provides a no-op context when Entra ID is not set up.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!isAuthConfigured) {
    return (
      <AuthContext.Provider value={defaultAuthContext}>
        {children}
      </AuthContext.Provider>
    );
  }
  return <MsalAuthProvider>{children}</MsalAuthProvider>;
};
